import React, { useEffect, useCallback, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import OnboardingLayout from '../layouts/OnboardingLayout';
import Button from 'components/v2/button/Button';
import InputField from 'components/v2/inputField/InputField';
import ProfileImageAvatar from '../ProfileImageAvatar';
import { RotateCcw, Loader, Check, CircleX } from 'lucide-react';
import { cn } from 'utils/helpers';
import { useLazyGetUsernameSuggestionsQuery } from 'modules/auth/services/user.mutation.service';
import { debounce } from 'lodash';

type Props = {
  onStartClick: () => void;
  onBackClick: () => void;
  userData?: {
    name: string;
    userName: string;
    photoURL: string | null;
  };
  onUserDataChange?: (
    data: Partial<{
      name: string;
      userName: string;
      photoURL: string | null;
    }>,
  ) => void;
  onboardingProgress: number;
};

const EnterYourDetails: React.FC<Props> = ({
  onStartClick,
  onBackClick,
  userData,
  onUserDataChange,
  onboardingProgress,
}) => {
  const { control, handleSubmit, watch, setValue } = useForm<{
    name: string;
    userName: string;
    photoURL: string | null;
    isUsernameValid: boolean | null;
    usernameSuggestions: string[];
  }>({
    defaultValues: {
      name: userData?.name || '',
      userName: userData?.userName || '',
      photoURL: userData?.photoURL || null,
      isUsernameValid: null,
      usernameSuggestions: [],
    },
  });

  const photoURL = watch('photoURL');
  const userName = watch('userName');
  const name = watch('name');

  const [
    triggerGetUsernameSuggestions,
    { data: userNameData, isLoading, isFetching },
  ] = useLazyGetUsernameSuggestionsQuery();

  const [triggerGetUsernameValidation, { data: userNameValidationData }] =
    useLazyGetUsernameSuggestionsQuery();

  const fetchUsernameSuggestions = useCallback(
    debounce((baseUsername: string) => {
      if (baseUsername) {
        triggerGetUsernameSuggestions({
          baseUsername: baseUsername.replace('@', ''),
        });
      }
    }, 500),
    [triggerGetUsernameSuggestions],
  );

  const fetchUsernameValidation = useCallback(
    debounce((baseUsername: string) => {
      if (baseUsername) {
        triggerGetUsernameValidation({
          baseUsername: baseUsername.replace('@', ''),
        });
      }
    }, 500),
    [triggerGetUsernameValidation],
  );

  useEffect(() => {
    if (userName) {
      fetchUsernameSuggestions(userName);
      fetchUsernameValidation(userName);
    }
  }, [userName, fetchUsernameSuggestions, fetchUsernameValidation]);

  useEffect(() => {
    if (userNameData?.suggestions) {
      setValue('usernameSuggestions', userNameData.suggestions);
    }
  }, [userNameData, setValue]);

  useEffect(() => {
    if (userNameValidationData) {
      setValue('isUsernameValid', userNameValidationData.matches);
    }
  }, [userNameValidationData, setValue]);

  // Remove the continuous update effect that was causing the infinite loop
  // We'll only update the parent component when specific actions occur

  const handleUsernameChange = (value: string) => {
    if (value === '' || value === '@') {
      setValue('userName', '');
      setValue('isUsernameValid', null);
    } else {
      setValue('userName', value.startsWith('@') ? value : `@${value}`);
    }
  };

  const getUsernameError = (
    userName: string,
    isUsernameValid: boolean | null,
  ) => {
    if (userName.length >= 1 && userName.length < 7) {
      return 'Username must be at least 6 characters long';
    }
    const specialCharacters = /[!@#$%^&*(),.?":{}|<>]/;
    if (specialCharacters.test(userName.replace('@', ''))) {
      return 'Username must not include special characters';
    }
    return isUsernameValid !== false && userName.length > 0
      ? 'Username already exists!'
      : undefined;
  };

  const onSubmit = () => {
    // Save final user data before moving to next step
    if (onUserDataChange) {
      onUserDataChange({
        name,
        userName,
        photoURL,
      });
    }
    onStartClick();
  };

  // Handle profile image change separately
  const handleProfileImageChange = (image: string) => {
    setValue('photoURL', image);
    if (onUserDataChange) {
      onUserDataChange({
        photoURL: image,
      });
    }
  };

  // Handle name change
  const handleNameChange = (value: string) => {
    setValue('name', value);
    if (onUserDataChange) {
      onUserDataChange({
        name: value,
      });
    }
  };

  const isButtonDisabled = useMemo(() => {
    return (
      name === '' ||
      userName === '' ||
      isLoading ||
      isFetching ||
      userName.length < 7 ||
      !!watch('isUsernameValid')
    );
  }, [name, userName, isLoading, isFetching, watch]);

  return (
    <OnboardingLayout
      onBackClick={onBackClick}
      actionChildren={
        <div>
          <Button
            className={cn(
              'transition-all duration-500',
              (name === '' ||
                userName === '' ||
                userName.length < 7 ||
                isLoading ||
                isFetching ||
                !!watch('isUsernameValid')) &&
                'bg-[#32395E]',
            )}
            disabled={isButtonDisabled}
            onClick={handleSubmit(onSubmit)}
          >
            Next
          </Button>
        </div>
      }
      onboardingProgress={onboardingProgress}
    >
      <div className="flex flex-col">
        <div className="flex flex-col gap-8">
          <div className="text-[2rem] font-bold">Enter your details</div>
          <div className="flex flex-col gap-8">
            <div className="flex items-end gap-2">
              <ProfileImageAvatar
                profileImage={photoURL}
                setProfileImage={handleProfileImageChange}
              />

              {photoURL && (
                <div
                  onClick={() => {
                    setValue('photoURL', null);
                    if (onUserDataChange) {
                      onUserDataChange({
                        photoURL: null,
                      });
                    }
                  }}
                  className="flex items-center gap-1 text-[0.875rem] text-red-500"
                >
                  <RotateCcw className="w-[1rem] h-[1rem]" /> Reset image
                </div>
              )}
            </div>
            <div className="flex flex-col gap-8 text-[0.875rem]">
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <InputField
                    {...field}
                    label="What's your name"
                    placeholder="Enter your name"
                    isRequired={true}
                    onChange={(value: string) => {
                      handleNameChange(value);
                      field.onChange(value);
                    }}
                  />
                )}
              />

              <div className="relative">
                <Controller
                  name="userName"
                  control={control}
                  render={({ field }) => (
                    <InputField
                      {...field}
                      value={
                        field.value.startsWith('@') || field.value === ''
                          ? field.value
                          : `@${field.value}`
                      }
                      onChange={(value: string) => {
                        handleUsernameChange(value);
                        field.onChange(value);
                        if (onUserDataChange) {
                          onUserDataChange({
                            userName: value.startsWith('@')
                              ? value
                              : `@${value}`,
                          });
                        }
                      }}
                      label="Your username"
                      placeholder={'Enter your @username'}
                      isRequired={true}
                      error={getUsernameError(
                        field.value,
                        watch('isUsernameValid'),
                      )}
                    />
                  )}
                />

                {userName && userName.length >= 6 && (
                  <div className="absolute right-4 top-[3.2rem]">
                    {isLoading || isFetching ? (
                      <Loader className="w-4 h-4 text-slate-300 animate-spin" />
                    ) : !watch('isUsernameValid') ? (
                      <Check className="w-4 h-4 text-green-600" />
                    ) : (
                      <CircleX className="w-4 h-4 text-red-600" />
                    )}
                  </div>
                )}
                {watch('usernameSuggestions')?.length > 0 &&
                  userName.length > 1 && (
                    <div className="w-full absolute left-0 flex flex-col bg-main whitespace-nowrap shadow-[1px_2px_10px_-5px_#ffffff71,-1px_2px_10px_0px_#ffffff71,0px_2px_10px_-6px_#ffffff71] text-white text-left font-normal leading-normal text-xs border-none mt-1 rounded-[0.75rem] z-[20] font-manrope">
                      <p className="uppercase text-[0.875rem] font-semibold text-gray-500 py-2 px-4">
                        Suggestions
                      </p>
                      {watch('usernameSuggestions').map((suggestion, index) => (
                        <div
                          key={index}
                          className="py-3 px-4 flex text-[0.875rem] font-medium items-center justify-between min-w-[13rem] cursor-pointer hover:bg-[#32395E]"
                          onClick={() => {
                            setValue('userName', suggestion);
                            if (onUserDataChange) {
                              onUserDataChange({
                                userName: suggestion,
                              });
                            }
                          }}
                        >
                          {`@${suggestion}`}
                        </div>
                      ))}
                    </div>
                  )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </OnboardingLayout>
  );
};

export default EnterYourDetails;
